/**
 * ManipulationProtectionSession — 3-minute protection & boundary session.
 * Phases: Intro → Bubbel-visualisatie → Zinnen → Ademhaling → Afsluiting
 */
import React, { useState, useEffect, useRef } from 'react';
import { C } from '../../../shared/theme';
import { s_GB } from '../../../shared/styles';

const COLOR = '#5B9ED0';   // cool blue — bescherming, helderheid, grens
const TOTAL = 180;

const PHASES = [
  { id: 0, start: 0,   end: 12  },  // Intro
  { id: 1, start: 12,  end: 75  },  // Stap 1 — bubbel-visualisatie
  { id: 2, start: 75,  end: 122 },  // Stap 2 — zinnen
  { id: 3, start: 122, end: 170 },  // Stap 3 — ademhaling
  { id: 4, start: 170, end: 180 },  // Afsluiting
];

const SENTENCES = [
  'Ik luister, maar ik neem niet over.',
  'Ik voel, maar ik draag het niet.',
];

// ── Bubble shield visualisation ───────────────────────────────────────────────
function BubbleShield({ active }) {
  const [tick, setTick] = useState(0);
  const rafRef  = useRef(null);
  const t0Ref   = useRef(Date.now());

  useEffect(() => {
    if (!active) { cancelAnimationFrame(rafRef.current); return; }
    t0Ref.current = Date.now();
    const loop = () => {
      setTick(Date.now() - t0Ref.current);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active]);

  const CENTER   = 110;   // px — half of container
  const SHIELD_R = 70;    // radius of transparent bubble
  const START_R  = 106;   // radius where pressure bubbles spawn

  // 10 incoming pressure bubbles at different angles, periods, sizes
  const bubbleDefs = [
    { angle:   0, period: 2800, offset:    0, size: 10 },
    { angle:  36, period: 3200, offset:  500, size:  8 },
    { angle:  72, period: 2500, offset: 1100, size: 12 },
    { angle: 108, period: 3500, offset:  300, size:  9 },
    { angle: 144, period: 2900, offset:  800, size: 11 },
    { angle: 180, period: 3100, offset:  200, size:  8 },
    { angle: 216, period: 2700, offset:  700, size: 10 },
    { angle: 252, period: 3400, offset: 1400, size:  9 },
    { angle: 288, period: 2600, offset:  600, size: 12 },
    { angle: 324, period: 3000, offset: 1000, size:  8 },
  ];

  return (
    <div style={{ position: 'relative', width: CENTER * 2, height: CENTER * 2, margin: '12px auto' }}>

      {/* Outer ambient ring */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        border: `1px solid ${COLOR}12`,
      }} />

      {/* Shield bubble — transparent/glass */}
      <div style={{
        position: 'absolute',
        left: CENTER - SHIELD_R, top: CENTER - SHIELD_R,
        width: SHIELD_R * 2, height: SHIELD_R * 2,
        borderRadius: '50%',
        background: `radial-gradient(circle at 38% 34%, ${COLOR}1A 0%, ${COLOR}06 60%, transparent 100%)`,
        border: `2px solid ${COLOR}55`,
        boxShadow: `0 0 24px ${COLOR}20, inset 0 0 28px ${COLOR}08`,
      }} />

      {/* Glass highlight — top-left shimmer */}
      <div style={{
        position: 'absolute',
        left: CENTER - SHIELD_R + 12, top: CENTER - SHIELD_R + 10,
        width: 28, height: 18,
        borderRadius: '50%',
        background: `radial-gradient(ellipse, ${COLOR}30 0%, transparent 70%)`,
        transform: 'rotate(-30deg)',
      }} />

      {/* Person icon (sitting, calm inside the bubble) */}
      <div style={{
        position: 'absolute',
        left: '50%', top: '50%',
        transform: 'translate(-50%, -52%)',
        fontSize: 38,
        lineHeight: 1,
        userSelect: 'none',
      }}>
        🧘
      </div>

      {/* Pressure bubbles — incoming, reflect off shield */}
      {bubbleDefs.map((b, i) => {
        const t  = ((tick + b.offset) % b.period) / b.period; // 0..1
        const rad = (b.angle * Math.PI) / 180;

        let r, opacity, scale;
        if (t < 0.58) {
          // travelling inward
          const p = t / 0.58;
          r       = START_R - (START_R - SHIELD_R) * p;
          opacity = 0.25 + p * 0.65;
          scale   = 0.5 + p * 0.5;
        } else if (t < 0.72) {
          // impact + bounce off shield
          const p = (t - 0.58) / 0.14;
          r       = SHIELD_R + (START_R - SHIELD_R) * 0.22 * p;
          opacity = 0.9 - p * 0.55;
          scale   = 1 - p * 0.3;
        } else {
          // fade away, drift outward
          const p = (t - 0.72) / 0.28;
          r       = SHIELD_R + (START_R - SHIELD_R) * (0.22 + 0.25 * p);
          opacity = 0.35 * (1 - p);
          scale   = 0.7 - p * 0.4;
        }

        const x = CENTER + r * Math.cos(rad) - b.size / 2;
        const y = CENTER + r * Math.sin(rad) - b.size / 2;

        return (
          <div key={i} style={{
            position: 'absolute',
            left: x, top: y,
            width: b.size, height: b.size,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(192,80,80,${opacity * 0.7}) 0%, transparent 70%)`,
            border: `1.5px solid rgba(192,80,80,${opacity})`,
            boxShadow: `0 0 ${b.size * 0.6}px rgba(192,80,80,${opacity * 0.5})`,
            transform: `scale(${scale})`,
            transformOrigin: 'center',
          }} />
        );
      })}
    </div>
  );
}

// ── Breathing circle (4s in · 8s out — extended exhale, releases absorbed pressure) ──
function ReleaseBreathCircle({ active }) {
  const [bs, setBs] = useState({ phase: 'in', progress: 0, secs: 4 });
  const ref = useRef(null);

  useEffect(() => {
    if (!active) { clearInterval(ref.current); return; }
    const DUR = { in: 4000, out: 8000 };
    let phase = 'in';
    let phaseStart = Date.now();

    ref.current = setInterval(() => {
      const now     = Date.now();
      const elapsed = now - phaseStart;
      if (elapsed >= DUR[phase]) {
        phase      = phase === 'in' ? 'out' : 'in';
        phaseStart = now;
      }
      const progress = Math.min(1, (now - phaseStart) / DUR[phase]);
      const secsLeft = Math.ceil((DUR[phase] - (now - phaseStart)) / 1000);
      setBs({ phase, progress, secs: Math.max(0, secsLeft) });
    }, 100);

    return () => clearInterval(ref.current);
  }, [active]);

  const scale = bs.phase === 'in' ? 0.45 + 0.55 * bs.progress : 1.0 - 0.55 * bs.progress;
  const BASE  = 80;
  const r     = BASE * scale;
  const glow  = Math.round(scale * 40);
  const label = bs.phase === 'in' ? 'Adem in — 4 sec' : 'Adem uit — 8 sec';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '16px 0 8px' }}>
      <div style={{ position: 'relative', width: BASE * 2 + 60, height: BASE * 2 + 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', width: BASE * 2 + 60, height: BASE * 2 + 60, borderRadius: '50%', border: `1px solid ${COLOR}18` }} />
        <div style={{ position: 'absolute', width: BASE * 2 + 30, height: BASE * 2 + 30, borderRadius: '50%', border: `1px solid ${COLOR}10` }} />
        <div style={{
          width: r * 2, height: r * 2, borderRadius: '50%',
          background: `radial-gradient(circle, ${COLOR}30 0%, ${COLOR}08 70%)`,
          border: `2px solid ${COLOR}${Math.round(scale * 99).toString(16).padStart(2, '0')}`,
          boxShadow: `0 0 ${glow}px ${COLOR}40, 0 0 ${glow * 2}px ${COLOR}15`,
          transition: 'width .15s ease-out, height .15s ease-out, box-shadow .15s ease',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ color: COLOR, fontSize: Math.max(10, Math.round(scale * 12)), fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center', padding: '0 6px', lineHeight: 1.3 }}>
            {label}
          </div>
          {bs.secs > 0 && (
            <div style={{ color: '#fff', fontSize: Math.max(16, Math.round(scale * 26)), fontWeight: 700, fontFamily: 'Inter,sans-serif', marginTop: 4 }}>
              {bs.secs}
            </div>
          )}
        </div>
      </div>
      <div style={{ color: C.silver, fontSize: 11, marginTop: 4 }}>Verlengde uitademing · laat los wat je hebt opgenomen</div>
    </div>
  );
}

// ── Rotating sentences with fadeUp ───────────────────────────────────────────
function RotatingSentence({ sentences, intervalSecs, elapsed }) {
  const idx     = Math.floor(elapsed / intervalSecs) % sentences.length;
  const animKey = Math.floor(elapsed / intervalSecs);

  return (
    <div style={{ minHeight: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
      <div
        key={animKey}
        className="fadeUp"
        style={{
          fontFamily: 'Georgia,serif',
          fontSize: 19,
          color: '#fff',
          textAlign: 'center',
          lineHeight: 1.55,
          padding: '12px 16px',
          borderLeft: `3px solid ${COLOR}`,
          paddingLeft: 18,
        }}
      >
        "{sentences[idx]}"
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ManipulationProtectionSession({ lang, onFinish }) {
  const nl = lang === 'nl';
  const [secsLeft, setSecsLeft] = useState(TOTAL);
  const [running, setRunning]   = useState(true);
  const [done, setDone]         = useState(false);
  const timerRef = useRef(null);

  const elapsed  = TOTAL - secsLeft;
  const phase    = PHASES.findIndex(p => elapsed >= p.start && elapsed < p.end);
  const curPhase = phase === -1 ? 4 : phase;

  const sentenceElapsed = curPhase === 2 ? elapsed - PHASES[2].start : 0;

  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setSecsLeft(s => {
        if (s <= 1) { clearInterval(timerRef.current); setDone(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const saveAndFinish = () => {
    const entry = {
      id:    Date.now(),
      type:  'manipulation_protection',
      label: nl ? 'Manipulatie & druk bescherming' : 'Manipulation & Pressure Protection',
      icon:  '🛡️',
      color: COLOR,
      date:  new Date().toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    };
    try {
      const prev = JSON.parse(localStorage.getItem('sl_saved_sessions') || '[]');
      prev.unshift(entry);
      if (prev.length > 50) prev.length = 50;
      localStorage.setItem('sl_saved_sessions', JSON.stringify(prev));
    } catch {}
    onFinish({ feelings: ['Beschermd', 'Losgelaten', 'Helder'], aiComment: null });
  };

  const mins = Math.floor(secsLeft / 60);
  const secs = secsLeft % 60;
  const pct  = ((TOTAL - secsLeft) / TOTAL) * 100;

  // ── Done screen ─────────────────────────────────────────────────────────────
  if (done || secsLeft === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🛡️</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, color: '#fff', marginBottom: 8 }}>
          {nl ? 'Sessie voltooid' : 'Session complete'}
        </div>
        <div style={{ color: COLOR, fontSize: 15, fontStyle: 'italic', marginBottom: 24, padding: '0 20px' }}>
          "Ik voel, maar ik draag het niet."
        </div>
        <button onClick={saveAndFinish} style={s_GB({ fontSize: 14, padding: '12px 28px' })}>
          {nl ? 'Opslaan & afsluiten' : 'Save & finish'}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Timer + progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,.08)', borderRadius: 4 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${COLOR},#7ec8e3)`, borderRadius: 4, transition: 'width 1s linear' }} />
        </div>
        <div style={{ color: C.silver, fontSize: 12, fontFamily: 'monospace', minWidth: 36 }}>
          {mins}:{String(secs).padStart(2, '0')}
        </div>
        <button onClick={() => setRunning(r => !r)} style={{ background: 'none', border: `1px solid rgba(255,255,255,.12)`, borderRadius: 8, color: C.silver, cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>
          {running ? '⏸' : '▶'}
        </button>
      </div>

      {/* Phase 0 — Intro */}
      {curPhase === 0 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            INTRODUCTIE
          </div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 17, color: '#fff', lineHeight: 1.55, marginBottom: 14 }}>
            Gebruik vóór of na contact waarbij je druk of manipulatie voelde
          </div>
          <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6 }}>
            Deze oefening helpt je om bij jezelf te blijven — open maar onaanraakbaar van binnenuit.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            {['sociale druk', 'manipulatie', 'grenzen stellen'].map(t => (
              <span key={t} style={{ background: `${COLOR}18`, border: `1px solid ${COLOR}33`, borderRadius: 20, color: COLOR, fontSize: 11, padding: '3px 10px' }}>{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Phase 1 — Bubble visualisation */}
      {curPhase === 1 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            STAP 1 — VISUALISEER JE BESCHERMBUBBEL
          </div>
          <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>
            Stel je voor: om je heen zit een transparante bubbel.<br />
            Jij bent rustig binnen. Alles van buiten komt binnen, maar raakt je niet direct.
          </div>
          <BubbleShield active={running && curPhase === 1} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8, justifyContent: 'center' }}>
            <span style={{ background: `rgba(192,80,80,0.12)`, border: `1px solid rgba(192,80,80,0.3)`, borderRadius: 20, color: '#c06060', fontSize: 11, padding: '3px 10px' }}>druk van buiten</span>
            <span style={{ background: `${COLOR}18`, border: `1px solid ${COLOR}44`, borderRadius: 20, color: COLOR, fontSize: 11, padding: '3px 10px' }}>jij blijft kalm</span>
          </div>
        </div>
      )}

      {/* Phase 2 — Zinnen */}
      {curPhase === 2 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            STAP 2 — ZEG DIT (HARDOP OF IN JE HOOFD)
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>
            Herhaal deze zinnen. Laat ze landen.
          </div>
          <RotatingSentence
            sentences={SENTENCES}
            intervalSecs={10}
            elapsed={sentenceElapsed}
          />
          {/* dot indicator */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14 }}>
            {SENTENCES.map((_, i) => {
              const activeIdx = Math.floor(sentenceElapsed / 10) % SENTENCES.length;
              return (
                <div key={i} style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: i === activeIdx ? COLOR : `${COLOR}33`,
                  transition: 'background 0.4s',
                }} />
              );
            })}
          </div>
          <div style={{ color: C.silver, fontSize: 11, textAlign: 'center', marginTop: 12 }}>
            Jij kiest wat je met je draagt.
          </div>
        </div>
      )}

      {/* Phase 3 — Breathing */}
      {curPhase === 3 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            STAP 3 — ADEM 5× LANGZAAM UIT
          </div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, color: '#fff', marginBottom: 4 }}>
            Adem in — en laat het langzaam los
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 4 }}>
            De verlengde uitademing geeft je zenuwstelsel het signaal: veilig, los, vrij.
          </div>
          <ReleaseBreathCircle active={running && curPhase === 3} />
        </div>
      )}

      {/* Phase 4 — Afsluiting */}
      {curPhase === 4 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '32px 20px', textAlign: 'center' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 16 }}>
            AFSLUITING
          </div>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🛡️</div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 19, color: '#fff', lineHeight: 1.65, fontStyle: 'italic', padding: '0 16px' }}>
            "Ik luister, maar ik neem niet over.<br />
            Ik voel, maar ik draag het niet."
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 16 }}>
            Je bubbel houdt stand. Jij ook.
          </div>
        </div>
      )}

      {/* Skip to finish early */}
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <button onClick={() => { clearInterval(timerRef.current); setDone(true); }}
          style={{ background: 'none', border: 'none', color: C.silver, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
          {nl ? 'Sessie afsluiten' : 'End session'}
        </button>
      </div>
    </div>
  );
}
